/* eslint-disable react-refresh/only-export-components */
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useMatches } from "react-router";

import { OverlayBoundary } from "~/components/overlay-boundary";
import { classifyBasketSubmission } from "~/hec/basket/basket-submission.logic";
import { isUncertainAddClaimed } from "~/hec/basket/uncertain-add-claims";
import { useBasketSubmissionSettled } from "~/hec/basket/use-basket-submission-settled";
import { useOverlaysReady } from "~/hec/hooks/useOverlaysReady";
import type { C_ProductInfo } from "~/lib/app.types";
import { isAddedToBasketModalSuppressed } from "~/lib/handle-utils";

// Re-exported for existing callers/tests; the rule lives in the basket domain.
export { shouldSurfaceBasketFailureToast } from "~/hec/basket/basket-submission.logic";

// COM-189 wave 4 (extends the COM-174 recs split): the whole modal UI —
// Dialog, product rows, ProductImage/ProductPrice and the price resolvers —
// now lives in added-to-basket-modal.tsx and loads lazily. ONLY the submission
// WATCHER below stays synchronous in the always-loaded layout chunk, so no
// basket submission can complete before it is mounted; the modal chunk is
// warmed on the overlays-ready signal and, at the latest, mounts when the
// first submission produces a payload (the submission's own round-trip
// covers most of the chunk fetch).
const AddedToBasketModal = lazy(() => import("./added-to-basket-modal"));

// Explicit shape mirrors the basket action's `add`/`addMany` success branch
// (basket.tsx:121-204). Defined manually because RR7's SerializeFrom widens
// boolean literals, which breaks Extract<..., { successfullyAdded: true }>.
export type AddedItem = {
  name: string;
  price: number;
  image: Array<{ link: string; alt?: string }>;
  productData: C_ProductInfo;
  quantity: number;
};

export type SuccessData = {
  successfullyAdded: true;
  basket: {
    productSubTotal?: number;
    productItems?: Array<{ quantity?: number | null }>;
  };
  addedItems: AddedItem[];
  quantity: number;
};

// — Main: AddedToBasketFlyout ————————————————————————————————————————————
// Renamed from "flyout" to a modal in SFCC-10893 but the export name is
// preserved for the single mount point at app/routes/layout.tsx.

export function AddedToBasketFlyout() {
  const location = useLocation();
  const suppressedByRoute = isAddedToBasketModalSuppressed(useMatches());

  const [open, setOpen] = useState(false);
  const [data, setData] = useState<SuccessData | null>(null);

  // Warm the modal chunk once the page is idle / on first interaction, so the
  // usual case (chunk resolved before the first add-to-cart settles) shows the
  // modal exactly as fast as the pre-split component did.
  const overlaysReady = useOverlaysReady();

  /**
   * COM-830 — driven by the basket submission itself, published once per
   * settled `/resources/basket` POST by the route's `clientAction`
   * (`~/hec/basket/basket-submission-events`).
   *
   * This used to scan `useFetchers()` for a basket fetcher with `data`. That
   * list holds IN-FLIGHT fetchers only; the scan worked solely because every
   * basket POST re-ran the page/layout loaders, which kept the fetcher
   * `loading` with its payload attached for the length of that revalidation.
   * Once COM-830 dropped it, react-router (7.16) still publishes a `loading`
   * frame, but it settles to idle within microtasks (React batches the frame
   * away) and the idle entry is deleted from `state.fetchers` right after
   * subscribers are notified — so no render saw the payload, the modal never
   * opened and the failure toasts never fired.
   *
   * One event per submission also retires the per-fetcher dedup latch the scan
   * needed (the "add to cart, click off, and it shows again" bug, Jam
   * 2026-08-16): nothing re-delivers a dismissed submission, and a genuine
   * second add is a new event. The form body (`action`, `sflMove`,
   * `suppressAddedToBasketModal`, …) rides the event, so those sentinels stay
   * readable after the fetcher has gone idle and dropped its formData.
   */
  useBasketSubmissionSettled(result => {
    const outcome = classifyBasketSubmission(result, {
      suppressedByRoute,
      uncertainAddOwned: isUncertainAddClaimed(
        result.formData.get("productId"),
      ),
    });
    if (outcome.kind === "toast-error") {
      toast.error(outcome.message);
      console.error("Basket action error", {
        action: outcome.intent,
        payload: outcome.payload,
      });
      return;
    }
    if (outcome.kind === "open-modal") {
      setData(outcome.data as SuccessData);
      setOpen(true);
    }
  });

  // Close the modal when the user navigates away (e.g. clicking View Cart).
  // Skip the initial mount tick — only react to subsequent pathname changes.
  const initialPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname === initialPathRef.current) return;
    initialPathRef.current = location.pathname;
    setOpen(false);
  }, [location.pathname]);

  // Closed Radix Dialog renders nothing, so pre-mounting on overlaysReady is
  // pure chunk warming; `data` forces the mount even if a submission lands
  // before the ready signal (first-interaction arming makes that rare — the
  // add-to-cart click itself arms it).
  if (!overlaysReady && !data) return null;

  return (
    // OverlayBoundary (PR #1042 blocker 3): a rejected modal chunk degrades
    // to no confirmation modal (the add itself already succeeded server-side)
    // instead of unmounting the page.
    <OverlayBoundary overlay="added-to-basket-modal" fallback={null}>
      <Suspense fallback={null}>
        <AddedToBasketModal open={open} onOpenChange={setOpen} data={data} />
      </Suspense>
    </OverlayBoundary>
  );
}
